import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";
import { supabase } from "@/integrations/supabase/client";

// Routes we care about — only public/auth/onboarding for the optimization
// follow-up. Authenticated app pages are excluded to reduce noise.
const TRACKED_PREFIXES = ["/", "/auth", "/onboarding", "/pricing", "/book"];

const normalizeRoute = (pathname: string): string | null => {
  if (pathname === "/") return "/";
  // Group dynamic booking slugs as /book/:slug to keep aggregates meaningful.
  if (pathname.startsWith("/book/")) return "/book/:slug";
  for (const prefix of TRACKED_PREFIXES) {
    if (prefix === "/") continue;
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return prefix;
  }
  return null;
};

const getDeviceType = (): string => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
  if (/iPad|Tablet/i.test(ua)) return "tablet";
  return "desktop";
};

const getConnectionType = (): string | null => {
  const conn = (navigator as any).connection;
  return conn?.effectiveType ?? null;
};

// Buffer metrics per page-load and flush together to minimize requests.
const buffer: Array<{
  route: string;
  metric_name: string;
  metric_value: number;
  metric_rating: string | null;
  navigation_type: string | null;
  device_type: string;
  connection_type: string | null;
  user_agent: string;
  release_tag: string;
}> = [];

let flushScheduled = false;
const scheduleFlush = () => {
  if (flushScheduled) return;
  flushScheduled = true;
  // Flush on page hide / unload — captures LCP/INP that fire late.
  const flush = () => {
    if (buffer.length === 0) return;
    const payload = buffer.splice(0, buffer.length);
    void supabase.from("web_vitals").insert(payload);
  };
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flush();
    });
    window.addEventListener("pagehide", flush);
  }
};

const handleMetric = (metric: Metric) => {
  const route = normalizeRoute(window.location.pathname);
  if (!route) return;
  buffer.push({
    route,
    metric_name: metric.name,
    metric_value: Math.round(metric.value * 1000) / 1000,
    metric_rating: metric.rating ?? null,
    navigation_type: metric.navigationType ?? null,
    device_type: getDeviceType(),
    connection_type: getConnectionType(),
    user_agent: navigator.userAgent.slice(0, 200),
    release_tag: typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "unknown",
  });
  scheduleFlush();
};

let initialized = false;
export const initWebVitals = () => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  // Skip Lovable's preview/sandbox to keep reports clean of dev traffic.
  if (window.location.hostname.includes("lovableproject.com")) return;
  if (window.location.hostname.includes("lovable.app") && !window.location.hostname.startsWith("tirevault")) return;
  onLCP(handleMetric);
  onCLS(handleMetric);
  onINP(handleMetric);
  onFCP(handleMetric);
  onTTFB(handleMetric);
};